import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, CalendarDays, ShoppingCart, BookOpen, Store, ArrowRight, Users, ShieldCheck, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { dashboardFor } from "@/context/AuthContext";
import { useFreeCountdown } from "@/lib/launchOffer";
import { HeroCountdown } from "@/components/HeroCountdown";

const FEATURES = [
  { icon: Store, title: "Local Vendor Directory", desc: "The Vendor Directory is the ultimate local guide for parents actively seeking trusted services, extra-murals, and venues. If you run a business that caters to families—such as a party venue, a tutoring service, a sports club, or a pediatric wellness practice—this is your dedicated space. Why Subscribe? Subscribing puts your business directly in front of parents who are actively looking to book activities and services for their children. It elevates your local visibility, helps you build lasting relationships within the community, and turns busy parents into loyal clients.", to: "/vendors", tint: "bg-sage-light text-sage-dark" },
  { icon: CalendarDays, title: "Events & Activities", desc: "The Event Creator suite is designed for organizers who bring the community together through larger-scale activities, workshops, and gatherings. Whether you are coordinating a holiday tech-camp, a local homeschooling expo, or a community sports day, this tool amplifies your reach and connects you instantly with interested families. Why Subscribe? Take the stress out of event promotion. A subscription allows you to effortlessly publish events to an audience actively searching for weekend activities and educational outings. Instead of managing complex booking systems on our end, we highlight families who are interested in your event and allow them to contact you directly to secure their spot. This helps you build genuine relationships, simplify your admin, and establish yourself as a key pillar in the local family community.", to: "/events", tint: "bg-terracotta-light text-terracotta-dark" },
  { icon: ShoppingCart, title: "Marketplace", desc: "The Marketplace is our vibrant, curated community shop focused exclusively on physical goods. It is the perfect storefront for businesses and local creators selling educational supplies, children's books, wooden toys, mom-made crafts, clothing, and everyday family essentials. Why Subscribe? The Marketplace cuts through the noise of general e-commerce by connecting you exclusively with a niche market of parents eager for quality, family-centric products. Subscribing allows you to turn your craft, retail business, or educational materials into a steady revenue stream supported by a community that values local, purpose-driven goods.", to: "/resources", tint: "bg-sage-light text-sage-dark" },
  { icon: BookOpen, title: "Resource Hub", desc: "The Resources section is an exclusive digital library carefully curated and uploaded directly by the Compass Kids team. We provide a growing collection of high-quality educational materials, printable worksheets, homeschooling aids, and parenting guides designed to support your family's unique needs. Why Join? Save time scouring the internet for reliable materials. By joining Compass Kids, you gain instant access to our trusted, in-house toolkit—giving you everything you need to enrich your children's learning, spark new activities, and simplify your daily routines, all in one convenient place.", to: "/vendors", tint: "bg-terracotta-light text-terracotta-dark" },
];

export default function Index() {
  const { user } = useAuth();
  const { left, label } = useFreeCountdown(1000);
  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/13004440/pexels-photo-13004440.jpeg"
            alt="Family hiking on a scenic trail"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-forest/85 via-forest/60 to-forest/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-2xl">
            <p className="fade-in text-terracotta-light font-heading font-bold text-sm uppercase tracking-widest mb-4">A community hub for every kid</p>
            <h1 className="fade-in-1 text-4xl sm:text-5xl lg:text-6xl font-black text-cream leading-tight mb-6">
              Every child deserves a friend.
            </h1>
            <p className="fade-in-2 text-base sm:text-lg text-cream/85 leading-relaxed mb-8 max-w-xl" data-testid="hero-subheading">
              <strong className="text-cream">Parent Profiles are 100% Free Forever.</strong> Vendor, Event Creator, and Marketplace listings get Free Early Access until 30 November 2026, then normal pricing applies.
            </p>
            {left && <HeroCountdown left={left} label={label} />}
            <div className="fade-in-3 flex flex-wrap gap-3">
              {user ? (
                <Link to={dashboardFor(user)}>
                  <Button size="lg" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-lg hover:-translate-y-0.5 transition-transform" data-testid="hero-dashboard-btn">
                    Go to my dashboard <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-lg hover:-translate-y-0.5 transition-transform" data-testid="hero-join-btn">
                      {left ? "Join free now" : "Sign Up"} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="grain py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase mb-2">What you'll find</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-forest">One compass, many paths</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`fade-in-${Math.min(i, 3)} group bg-white/70 border border-sage/20 rounded-3xl p-6 shadow-sm`}
                data-testid={`feature-card-${i}`}
              >
                <span className={`inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-4 ${f.tint}`}>
                  <f.icon className="w-6 h-6" />
                </span>
                <h3 className="font-heading font-bold text-lg text-forest mb-2">{f.title}</h3>
                <p className="text-sm text-forest/70 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-softstone/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl overflow-hidden shadow-md">
            <img
              src="https://customer-assets-v7afamib.emergentagent.net/job_f630a2e0-2c75-4e5e-b01b-1815bce746a6/artifacts/5tq3twhg_Image%201.png"
              alt="Families and kids playing tug-of-war together in the park"
              className="w-full h-72 sm:h-96 object-cover"
            />
          </div>
          <div>
            <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase mb-2">For families</p>
            <h2 className="text-2xl sm:text-3xl font-black text-forest mb-4">Learning is better together</h2>
            <p className="text-forest/75 leading-relaxed mb-6">
              From muddy-boots nature hikes to ice-rink meet-ups, Compass Kids makes it easy to find your people. Host a meet-up at a local spot, RSVP for events, message local vendors — whether you homeschool or not, every child is welcome here.
            </p>
            <div className="space-y-3">
              {[
                { icon: Users, text: "Meet other families and kids near you" },
                { icon: MapPin, text: "Find local meet-ups, tutors and activity providers" },
                { icon: Heart, text: "A safe, moderated space for your family" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3 text-sm text-forest/80">
                  <span className="w-8 h-8 rounded-full bg-sage-light flex items-center justify-center shrink-0">
                    <item.icon className="w-4 h-4 text-sage-dark" />
                  </span>
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-cream" data-testid="about-sections">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <article className="bg-white/70 border border-sage/20 rounded-3xl p-8 sm:p-10 shadow-sm" data-testid="about-who">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-3xl" aria-hidden="true">🧭</span>
              <h2 className="text-2xl sm:text-3xl font-black text-forest">Who is Compass Kids?</h2>
            </div>
            <p className="text-forest/80 leading-relaxed">
              Think of us as the <strong className="text-forest">central hub for your family's social and educational life</strong>. We are a vibrant, local community designed to make childhood more <strong className="text-forest">connected, fun, and affordable</strong>. Whether you are navigating the homeschooling journey or simply looking for a better way for your child to make genuine, lasting friendships outside the school gates, we make it simple. Beyond the social buzz, we provide instant access to a <strong className="text-forest">curated local marketplace</strong>, connecting you with vetted suppliers and activities right in your neighborhood.
            </p>
          </article>

          <article className="bg-sage-light/40 border border-sage/20 rounded-3xl p-8 sm:p-10 shadow-sm" data-testid="about-for">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-3xl" aria-hidden="true">👨‍👩‍👧‍👦</span>
              <h2 className="text-2xl sm:text-3xl font-black text-forest">Who is Compass Kids For?</h2>
            </div>
            <p className="text-forest/80 leading-relaxed mb-6">
              While we specialize in empowering homeschooling families, Compass Kids is for <strong className="text-forest">every child who needs a place to belong</strong>.
            </p>
            <ul className="space-y-4 text-forest/80 leading-relaxed">
              <li className="flex gap-3">
                <span className="w-2 h-2 rounded-full bg-sage-dark mt-2 shrink-0" />
                <span><strong className="text-forest">The Homeschooling Community:</strong> Find your daily support system, co-ops, and social circles.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-2 h-2 rounded-full bg-sage-dark mt-2 shrink-0" />
                <span><strong className="text-forest">The Social Seekers:</strong> For kids in traditional schools who haven't quite found their tribe yet and need a low-pressure, fun environment to make new friends.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-2 h-2 rounded-full bg-sage-dark mt-2 shrink-0" />
                <span><strong className="text-forest">Weekend Adventurers:</strong> For families who want to break out of the school-home routine and engage in meaningful, local activities.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-2 h-2 rounded-full bg-sage-dark mt-2 shrink-0" />
                <span><strong className="text-forest">All Families:</strong> No matter how you educate, if you want a supportive network and a vibrant social outlet for your kids, there is a seat at the table for you.</span>
              </li>
            </ul>
          </article>

          <article className="bg-terracotta-light/40 border border-terracotta/20 rounded-3xl p-8 sm:p-10 shadow-sm" data-testid="about-why">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-3xl" aria-hidden="true">🚀</span>
              <h2 className="text-2xl sm:text-3xl font-black text-forest">Why Sign Up for Compass Kids?</h2>
            </div>
            <p className="text-forest/80 leading-relaxed mb-6">
              Sign up for <strong className="text-terracotta-dark">free</strong> and unlock a shortcut to a more connected lifestyle. We provide the structure so you can focus on the memories.
            </p>
            <ul className="space-y-4 text-forest/80 leading-relaxed">
              <li className="flex gap-3">
                <span className="w-2 h-2 rounded-full bg-terracotta mt-2 shrink-0" />
                <span><strong className="text-forest">A Safe Social Outlet:</strong> Access private community channels to organize playdates and meetups. It's the easiest way to find your people in a focused, local environment without the noise of massive social media groups.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-2 h-2 rounded-full bg-terracotta mt-2 shrink-0" />
                <span><strong className="text-forest">Exclusive Savings:</strong> Gain entry to our <strong className="text-forest">Member-Only Deals</strong>. From deep discounts on local outings and educational workshops to special rates from vetted vendors, your membership pays for itself through the savings you'll find every month.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-2 h-2 rounded-full bg-terracotta mt-2 shrink-0" />
                <span><strong className="text-forest">Vetted & Local:</strong> Stop searching aimlessly. We've curated the best local activities and resources so you can quickly find <strong className="text-forest">high-quality social and learning opportunities</strong> for your kids.</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase mb-2">For Businesses</p>
            <h2 className="text-2xl sm:text-3xl font-black text-forest mb-4">Grow Your Business with Compass Kids</h2>
            <p className="text-forest/75 leading-relaxed mb-6">
              Looking to reach more families with your tutoring, events, activities, or products? Join the Compass Kids marketplace. Create your vendor profile to share flyers, photos, and listings directly with our community.
            </p>
            <Link to="/register">
              <Button className="rounded-full bg-sage hover:bg-sage-dark text-white shadow-md hover:-translate-y-0.5 transition-transform" data-testid="vendor-cta-btn">
                Become a Business Partner <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="order-1 lg:order-2 rounded-3xl overflow-hidden shadow-md">
            <img
              src="https://images.pexels.com/photos/7869791/pexels-photo-7869791.jpeg"
              alt="Children doing crafts together"
              className="w-full h-72 sm:h-96 object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
